import React, { useEffect, useState } from "react";
import { Button, Flex, Heading, Notification, Spinner, TextField } from "@telia/teddy";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../models";
import { API_URL_GET_DEALER, API_URL_UPDATE_DEALER } from "../../api";
import { retailerFetch } from "../../retailerFetch";
import { getSalesAgent } from "../../state/selectors/selectors";
import { DealerInfo } from "../../types/models";

type DealerUpdateForm = {
    streetAddress: string;
    streetPostalNo: string;
    streetPostalCity: string;
    postalAddress: string;
    postalNo: string;
    postalCity: string;
    phone: string;
    contactEmail: string;
};

const emptyFormState: DealerUpdateForm = {
    streetAddress: "",
    streetPostalNo: "",
    streetPostalCity: "",
    postalAddress: "",
    postalNo: "",
    postalCity: "",
    phone: "",
    contactEmail: "",
};

const tableStyle = { borderCollapse: "collapse" } as const;
const labelCellStyle = {
    padding: "0.4rem 0.75rem 0.4rem 0",
    fontFamily: "TeliaSans, sans-serif",
    fontWeight: 700,
    whiteSpace: "nowrap",
    verticalAlign: "top",
    width: "220px",
} as const;
const valueCellStyle = { padding: "0.4rem 0", verticalAlign: "top" } as const;

const toFormData = (dealer: DealerInfo): DealerUpdateForm => ({
    streetAddress: dealer.streetAddress ?? "",
    streetPostalNo: dealer.streetPostalNo ?? "",
    streetPostalCity: dealer.streetPostalCity ?? "",
    postalAddress: dealer.postalAddress ?? "",
    postalNo: dealer.postalNo ?? "",
    postalCity: dealer.postalCity ?? "",
    phone: dealer.phone ?? "",
    contactEmail: dealer.contactEmail ?? "",
});

const formatAddress = (
    address?: string | null,
    postalNo?: string | null,
    postalCity?: string | null,
): string | undefined =>
    [address, [postalNo, postalCity].filter(Boolean).join(" ")].filter(Boolean).join(", ") || undefined;

const InfoRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
    if (value === undefined || value === null || value === "") return null;
    return (
        <tr>
            <td style={labelCellStyle}>{label}</td>
            <td style={valueCellStyle}>{value}</td>
        </tr>
    );
};

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Heading as="h2" variant="title-100" mt="400" mb="100">
        {children}
    </Heading>
);

const Butikk: React.FC = () => {
    const [dealer, setDealer] = useState<DealerInfo | null>(null);
    const [formData, setFormData] = useState(emptyFormState);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const salesAgent = useSelector((state: RootState) => getSalesAgent(state));
    const isManager = salesAgent?.isManager === true;

    useEffect(() => {
        retailerFetch(API_URL_GET_DEALER)
            .then((res) => {
                if (!res.ok) throw new Error(`Feil ved henting av butikkinformasjon (${res.status})`);
                return res.json() as Promise<{ dealer: DealerInfo }>;
            })
            .then((data) => {
                setDealer(data.dealer);
                setFormData(toFormData(data.dealer));
            })
            .catch((err: Error) => setError(err.message))
            .finally(() => setLoading(false));
    }, []);

    const handleFieldChange = (field: keyof DealerUpdateForm) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const handleStartEditing = () => {
        if (!dealer) return;
        setFormData(toFormData(dealer));
        setSaveError(null);
        setIsEditing(true);
    };

    const handleCancelEditing = () => {
        if (dealer) {
            setFormData(toFormData(dealer));
        }
        setSaveError(null);
        setIsEditing(false);
    };

    const handleSave = async () => {
        setIsSaving(true);
        setSaveError(null);

        try {
            const response = await retailerFetch(API_URL_UPDATE_DEALER, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                const errorBody = (await response.json().catch(() => null)) as { error?: string } | null;
                throw new Error(errorBody?.error || `Feil ved oppdatering av butikkinformasjon (${response.status})`);
            }

            const data = (await response.json()) as { dealer: DealerInfo };
            setDealer(data.dealer);
            setFormData(toFormData(data.dealer));
            setIsEditing(false);
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : "Kunne ikke oppdatere butikkinformasjon.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="retailer-dashboard-wrapper">
            {loading && (
                <Flex justify="center" pt="400">
                    <Spinner size="lg" />
                </Flex>
            )}

            {error && (
                <Notification type="tile" variant="error" mt="300">
                    <Notification.Icon />
                    <Notification.Text>{error}</Notification.Text>
                </Notification>
            )}

            {saveError && (
                <Notification type="tile" variant="error" mt="300">
                    <Notification.Icon />
                    <Notification.Text>{saveError}</Notification.Text>
                </Notification>
            )}

            {!loading && !error && dealer && (
                <>
                    <table style={tableStyle}>
                        <tbody>
                            <InfoRow label="Butikknavn" value={dealer.name} />
                            <InfoRow label="Kjede" value={dealer.chainName} />
                            <InfoRow label="Fylke" value={dealer.countyName} />
                            {dealer.dealerGroup && <InfoRow label="Forhandlergruppe" value={dealer.dealerGroup} />}
                        </tbody>
                    </table>

                    <SectionHeading>Kontaktpersoner</SectionHeading>
                    <table style={tableStyle}>
                        <tbody>
                            <InfoRow label="Kontaktperson" value={dealer.contactName} />
                            <InfoRow label="Telia-kontakt" value={dealer.netcomContactName} />
                        </tbody>
                    </table>

                    <SectionHeading>Kontaktinformasjon</SectionHeading>
                    <table style={tableStyle}>
                        <tbody>
                            <InfoRow
                                label="Telefon"
                                value={
                                    isEditing ? (
                                        <TextField>
                                            <TextField.Input
                                                aria-label="Telefon"
                                                value={formData.phone}
                                                onChange={handleFieldChange("phone")}
                                                inputMode="numeric"
                                            />
                                        </TextField>
                                    ) : (
                                        dealer.phone
                                    )
                                }
                            />
                            <InfoRow
                                label="E-post"
                                value={
                                    isEditing ? (
                                        <TextField>
                                            <TextField.Input
                                                aria-label="E-post"
                                                value={formData.contactEmail}
                                                onChange={handleFieldChange("contactEmail")}
                                                type="email"
                                            />
                                        </TextField>
                                    ) : (
                                        dealer.contactEmail
                                    )
                                }
                            />
                        </tbody>
                    </table>

                    <SectionHeading>Adresse</SectionHeading>
                    <table style={tableStyle}>
                        <tbody>
                            <InfoRow
                                label="Besøksadresse"
                                value={
                                    isEditing ? (
                                        <Flex direction="column" gap="200" style={{ maxWidth: "420px" }}>
                                            <TextField>
                                                <TextField.Label>Adresse</TextField.Label>
                                                <TextField.Input
                                                    value={formData.streetAddress}
                                                    onChange={handleFieldChange("streetAddress")}
                                                />
                                            </TextField>
                                            <Flex gap="200">
                                                <TextField>
                                                    <TextField.Label>Postnummer</TextField.Label>
                                                    <TextField.Input
                                                        value={formData.streetPostalNo}
                                                        onChange={handleFieldChange("streetPostalNo")}
                                                        inputMode="numeric"
                                                    />
                                                </TextField>
                                                <TextField>
                                                    <TextField.Label>Poststed</TextField.Label>
                                                    <TextField.Input
                                                        value={formData.streetPostalCity}
                                                        onChange={handleFieldChange("streetPostalCity")}
                                                    />
                                                </TextField>
                                            </Flex>
                                        </Flex>
                                    ) : (
                                        formatAddress(
                                            dealer.streetAddress,
                                            dealer.streetPostalNo,
                                            dealer.streetPostalCity,
                                        )
                                    )
                                }
                            />
                            <InfoRow
                                label="Postadresse"
                                value={
                                    isEditing ? (
                                        <Flex direction="column" gap="200" style={{ maxWidth: "420px" }}>
                                            <TextField>
                                                <TextField.Label>Adresse</TextField.Label>
                                                <TextField.Input
                                                    value={formData.postalAddress}
                                                    onChange={handleFieldChange("postalAddress")}
                                                />
                                            </TextField>
                                            <Flex gap="200">
                                                <TextField>
                                                    <TextField.Label>Postnummer</TextField.Label>
                                                    <TextField.Input
                                                        value={formData.postalNo}
                                                        onChange={handleFieldChange("postalNo")}
                                                        inputMode="numeric"
                                                    />
                                                </TextField>
                                                <TextField>
                                                    <TextField.Label>Poststed</TextField.Label>
                                                    <TextField.Input
                                                        value={formData.postalCity}
                                                        onChange={handleFieldChange("postalCity")}
                                                    />
                                                </TextField>
                                            </Flex>
                                        </Flex>
                                    ) : (
                                        formatAddress(dealer.postalAddress, dealer.postalNo, dealer.postalCity)
                                    )
                                }
                            />
                        </tbody>
                    </table>

                    {isManager && (
                        <Flex gap="200" mt="400" justify="end">
                            {isEditing ? (
                                <>
                                    <Button variant="secondary" onClick={handleCancelEditing} disabled={isSaving}>
                                        Avbryt
                                    </Button>
                                    <Button variant="primary" onClick={handleSave} loading={isSaving}>
                                        Lagre
                                    </Button>
                                </>
                            ) : (
                                <Button variant="primary" onClick={handleStartEditing}>
                                    Endre
                                </Button>
                            )}
                        </Flex>
                    )}
                </>
            )}
        </div>
    );
};

export default Butikk;
